import { DataTableProvider } from 'components/DataTable'
import React from 'react'
import { Tabs } from '../../../components/FbUI/Tabs'
import styled from 'styled-components/macro'
import { Page } from 'components/FbUI/Page'
import { FaPlus } from 'react-icons/fa'
import { CgSoftwareUpload } from 'react-icons/cg'
import {
  ContactsTable,
  ContactsTableRef,
} from 'components/Tables/ContactsTable/ContactsTable'
import NewContactModal from '../../../components/Modals/NewContactModal'
import ImportContactModal from 'components/ImportContactFlow/ImportContactModal'
import useDocumentTitle from 'components/useDocumentTitle'
import FbButton from 'components/FbUI/FbButton'
import { useSimpleTabSelector } from 'utils/useSimpleTabSelector'
import { useQueryClient } from '@tanstack/react-query'
import { CONTACTS_TABLE_KEY } from '../../../constants/tableQueryKeys'

export function ContactsPage() {
  const queryClient = useQueryClient()
  const [selectedTab, setSelectedTab] = useSimpleTabSelector<
    'all-contacts' | 'my-contacts'
  >('all-contacts')

  const [totalContacts, setTotalContacts] = React.useState<number | null>(null)
  const [totalMyContacts, setTotalMyContacts] = React.useState<number | null>(
    null
  )
  const [showNewContactModal, setShowNewContactModal] = React.useState(false)
  const [showImportContactModal, setShowImportContactModal] =
    React.useState(false)
  const contactsTableRef = React.useRef<ContactsTableRef>(null)
  const myContactsTableRef = React.useRef<ContactsTableRef>(null)

  const handleContactCreated = () => {
    contactsTableRef.current?.refetch()
    myContactsTableRef.current?.refetch()
    queryClient.invalidateQueries(['tags', 'contacts'])
  }

  const handleContactUpdated = () => {
    contactsTableRef.current?.refetch()
    myContactsTableRef.current?.refetch()
  }

  useDocumentTitle(
    `${
      selectedTab === 'my-contacts' ? 'My' : 'All'
    } Contacts - Contacts - First Bite`
  )

  return (
    <Page>
      <Header>
        <TitleRow>
          <Title>Contacts</Title>

          <ButtonsContainer>
            <Button onClick={() => setShowNewContactModal(true)}>
              <FaPlus size={14} /> Add Contact
            </Button>
            <Button
              onClick={() => setShowImportContactModal(true)}
              variant="secondary"
            >
              <CgSoftwareUpload size={18} /> <span>Upload Contacts</span>
            </Button>
          </ButtonsContainer>
        </TitleRow>

        {showImportContactModal && (
          <ImportContactModal
            show={showImportContactModal}
            handleClose={() => setShowImportContactModal(false)}
            onContactsImported={handleContactCreated}
          />
        )}
        {showNewContactModal && (
          <NewContactModal
            show={showNewContactModal}
            handleClose={() => setShowNewContactModal(false)}
            onContactCreated={handleContactCreated}
          />
        )}

        <TabsAndStats>
          <Tabs
            tabs={[
              {
                key: 'all-contacts',
                title: (
                  <div>
                    <b>All contacts</b>{' '}
                    {totalContacts ? `(${totalContacts.toLocaleString()})` : ''}
                  </div>
                ),
              },
              {
                key: 'my-contacts',
                title: (
                  <div>
                    <b>My contacts</b>{' '}
                    {totalMyContacts
                      ? `(${totalMyContacts.toLocaleString()})`
                      : ''}
                  </div>
                ),
              },
            ]}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            tabStyle={{ width: '180px' }}
          />
        </TabsAndStats>
      </Header>

      <TableContainer show={selectedTab === 'all-contacts'}>
        {selectedTab === 'all-contacts' && (
          <DataTableProvider tableKey={CONTACTS_TABLE_KEY}>
            <ContactsTable
              setTotalRowsCount={setTotalContacts}
              ref={contactsTableRef}
              handleContactUpdated={handleContactUpdated}
            />
          </DataTableProvider>
        )}
      </TableContainer>
      <TableContainer show={selectedTab === 'my-contacts'}>
        {selectedTab === 'my-contacts' && (
          <DataTableProvider tableKey={'my-contacts-table'}>
            <ContactsTable
              setTotalRowsCount={setTotalMyContacts}
              ref={myContactsTableRef}
              myContactsOnly
              handleContactUpdated={handleContactUpdated}
            />
          </DataTableProvider>
        )}
      </TableContainer>
    </Page>
  )
}

const Button = styled(FbButton)`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 14px;

  gap: 8px;

  height: 44px;

  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`

const Header = styled.div`
  padding: 16px 25px 0 25px;
`

const TabsAndStats = styled.div`
  display: flex;
  justify-content: stretch;
  align-items: flex-end;
  margin-bottom: 20px;
  gap: 100px;
`

export const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 44px;
  margin-bottom: 12px;
`

export const Title = styled.h1`
  font-weight: 600;
  font-size: 24px;
  line-height: 28px;
  color: #101828;
`

// table container height based on
// footer height + navbar height + table header height
const TableContainer = styled.div<{
  show?: boolean
}>`
  display: ${(props) => (props.show ? 'flex' : 'none')};
  flex-direction: column;
  flex-shrink: 1;
  overflow: hidden;
  height: calc(100vh - 260px);
`
